import React from 'react';
import { ArrowRight, ExternalLink, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Opportunity } from '../../types';
import { DeadlineProgressBar } from './DeadlineProgressBar';
import { useSavedOpportunities } from '../../hooks/useSavedOpportunities';
import { safeExternalUrl } from '../../lib/safeExternalUrl';

function statusLabel(opp: Opportunity): string {
  if (opp.displayStatus === 'closing_soon') return 'Closing soon';
  if (opp.displayStatus === 'new' || opp.isNew) return 'New';
  if (opp.displayStatus === 'closed') return 'Closed';
  return 'Applications open';
}

function statusClass(opp: Opportunity): string {
  if (opp.displayStatus === 'closing_soon' || opp.isClosingSoon) return 'is-closing';
  if (opp.displayStatus === 'new' || opp.isNew) return 'is-new';
  if (opp.displayStatus === 'closed') return 'is-closed';
  return 'is-open';
}

function formatDate(value?: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function OpportunityCard({
  opportunity,
  featured = false,
}: {
  opportunity: Opportunity;
  featured?: boolean;
}) {
  const navigate = useNavigate();
  const { isSaved, toggleSave } = useSavedOpportunities();
  const saved = isSaved(opportunity.id);
  const applyLink = safeExternalUrl(opportunity.officialApplyUrl || opportunity.officialWebsite);

  return (
    <article className={`wfx-opportunity-card glass-surface${featured ? ' is-featured' : ''}`}>
      <div className="wfx-opportunity-card-head">
        <div className="wfx-opportunity-badges">
          <span className={`chip-pill wfx-opp-status ${statusClass(opportunity)}`}>{statusLabel(opportunity)}</span>
          <span className="chip-pill">{opportunity.qualification}</span>
        </div>
        <button
          type="button"
          className={`wfx-save-btn ${saved ? 'is-saved' : ''}`}
          aria-label={saved ? 'Remove saved opportunity' : 'Save opportunity'}
          onClick={() =>
            toggleSave({
              id: opportunity.id,
              slug: opportunity.slug,
              title: opportunity.title,
              organization: opportunity.organization,
              savedAt: new Date().toISOString(),
            })
          }
        >
          <Heart size={18} fill={saved ? 'currentColor' : 'none'} />
        </button>
      </div>

      <h3>{opportunity.title}</h3>
      <p className="wfx-muted">{opportunity.organization}</p>

      <DeadlineProgressBar opportunity={opportunity} compact />

      <dl className="wfx-opportunity-meta">
        <div>
          <dt>Last date</dt>
          <dd>{formatDate(opportunity.applicationEnd)}</dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd>{opportunity.location || opportunity.state}</dd>
        </div>
        {opportunity.vacancies != null && (
          <div>
            <dt>Vacancies</dt>
            <dd>{opportunity.vacancies.toLocaleString('en-IN')}</dd>
          </div>
        )}
      </dl>

      <div className="wfx-result-card-actions">
        <button
          type="button"
          className="primary-action compact"
          onClick={() => navigate(`/opportunities/${opportunity.slug}`)}
        >
          View details <ArrowRight size={16} />
        </button>
        {applyLink && opportunity.displayStatus !== 'closed' && (
          <a href={applyLink} className="secondary-action compact" target="_blank" rel="noopener noreferrer">
            Apply now <ExternalLink size={14} />
          </a>
        )}
      </div>
    </article>
  );
}

export function OpportunityCountdownCard({ opportunity }: { opportunity: Opportunity }) {
  const navigate = useNavigate();
  const applyLink = safeExternalUrl(opportunity.officialApplyUrl || opportunity.officialWebsite);
  const progress = opportunity.deadlineProgress;
  const daysLabel = progress && opportunity.displayStatus !== 'closed'
    ? `${progress.daysRemaining}d`
    : opportunity.daysRemaining != null && opportunity.daysRemaining >= 0
      ? `${opportunity.daysRemaining}d`
      : null;

  return (
    <article className="wfx-opp-countdown glass-surface">
      <div className="wfx-opp-countdown-top">
        <div>
          <strong>{opportunity.title}</strong>
          <p className="wfx-muted">{opportunity.organization}</p>
        </div>
        {daysLabel && <div className="wfx-opp-countdown-days">{daysLabel}</div>}
      </div>
      <DeadlineProgressBar opportunity={opportunity} compact />
      <div className="wfx-opp-countdown-actions">
        <button type="button" className="text-action" onClick={() => navigate(`/opportunities/${opportunity.slug}`)}>
          Details
        </button>
        {applyLink && opportunity.displayStatus !== 'closed' && (
          <a href={applyLink} className="primary-action compact" target="_blank" rel="noopener noreferrer">
            Apply
          </a>
        )}
      </div>
    </article>
  );
}
